import { Treemap, hierarchy, treemapSquarify } from '@visx/hierarchy';
import {
  type PropsWithChildren,
  forwardRef,
  useMemo,
  type JSX,
  type MutableRefObject,
} from 'react';

import { TreeMapCanvas } from './TreeMapCanvas.js';
import { TreeMapCluster } from './TreeMapCluster.js';
import { Canvas } from '../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../core/components/canvas/CanvasContext.js';
import { CanvasShape } from '../../core/components/canvas/types/canvas-shape.js';
import { useGraphSize } from '../../core/components/chart-layout/hooks/useGraphSize.js';
import { TREEMAP_CHART_ARIA_LABEL } from '../constants.js';
import { useLabelsDisplay } from '../context/LabelsDisplayContext.js';
import { useTreeMapAggregationData } from '../context/TreeMapAggregationDataContext.js';
import { useTreeMapHighlightingMap } from '../context/TreeMapHighlightingMapContext.js';
import { useHighlightedState } from '../store/selectors.js';
import {
  nodePadding,
  nodePaddingOuter,
  nodePaddingTop,
} from '../utils/node-padding.js';
import { withParentLabel } from '../utils/with-parent-label.js';

export const TreeMapInternal = forwardRef<HTMLDivElement, PropsWithChildren>(
  (props, forwardedRef): JSX.Element => {
    const { children } = props;
    const { width, height } = useGraphSize();
    const roundedWidth = Math.floor(width);
    const roundedHeight = Math.floor(height);
    const labelsDisplay = useLabelsDisplay();
    const { dataForRendering } = useTreeMapAggregationData();
    const highlightingMap = useTreeMapHighlightingMap();

    const hasParentLabels = withParentLabel(labelsDisplay);

    // build hierarchy with concrete coordinates and width/size of nodes based on tree structure
    const root = useMemo(() => hierarchy(dataForRendering), [dataForRendering]);

    const highlightedItems = useHighlightedState();

    return (
      <div ref={forwardedRef} aria-label={TREEMAP_CHART_ARIA_LABEL} role="img">
        {children}
        <CanvasContext.Provider
          value={{
            shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
          }}
        >
          <Canvas width={roundedWidth} height={roundedHeight}>
            <Treemap
              root={root}
              size={[roundedWidth, roundedHeight]}
              tile={treemapSquarify}
              padding={(node) => nodePadding(node, hasParentLabels)}
              paddingTop={
                hasParentLabels ? (node) => nodePaddingTop(node) : undefined
              }
              paddingOuter={
                hasParentLabels ? (node) => nodePaddingOuter(node) : undefined
              }
              round
            >
              {(rootNode) => (
                <>
                  {hasParentLabels && (
                    <TreeMapCluster
                      treeMapData={rootNode}
                      highlightedItems={highlightedItems}
                      highlightingMap={highlightingMap}
                    />
                  )}
                  <TreeMapCanvas
                    // eslint-disable-next-line @typescript-eslint/prefer-ts-expect-error, @typescript-eslint/ban-ts-comment
                    // @ts-ignore Strict mode transition - needs to be investigated
                    treeMapData={rootNode}
                    highlightedItems={highlightedItems}
                    highlightingMap={highlightingMap}
                  />
                </>
              )}
            </Treemap>
          </Canvas>
        </CanvasContext.Provider>
      </div>
    );
  },
);
